import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import RefreshIcon from '@mui/icons-material/Refresh'
import { supabase } from '../../config/supabase'
import { useAuth } from '../../hooks/useAuth'
import { ContactMessage } from '../../types'

// Messages longer than this are cut with a "Vis mere" toggle
const PREVIEW_LENGTH = 140
const MAX_ROWS = 1000

const FORM_LABELS: Record<ContactMessage['form'], string> = {
  privatperson: 'Privatperson',
  bedemand: 'Bedemand',
}

export default function ContactMessages() {
  const { isAdmin } = useAuth()
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [messageToDelete, setMessageToDelete] = useState<ContactMessage | null>(null)
  const [error, setError] = useState<string | null>(null)

  const messagesQuery = useQuery({
    queryKey: ['contact_messages'],
    enabled: isAdmin,
    // New messages arrive all the time, so always refetch when the page opens
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contact_messages')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(MAX_ROWS)

      if (error) throw error
      return (data || []) as ContactMessage[]
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (message: ContactMessage) => {
      const { data, error } = await supabase
        .from('contact_messages')
        .delete()
        .eq('id', message.id)
        .select('id')

      if (error) throw error
      if (!data || data.length === 0) throw new Error('Henvendelsen blev ikke slettet (mangler rettigheder?)')
    },
    onSuccess: (_, message) => {
      queryClient.setQueryData<ContactMessage[]>(['contact_messages'], (prev) =>
        (prev || []).filter((m) => m.id !== message.id)
      )
      setMessageToDelete(null)
    },
    onError: (err: any) => {
      setError(`Kunne ikke slette: ${err.message}`)
      setMessageToDelete(null)
    },
  })

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (!isAdmin) return null

  if (messagesQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  const messages = messagesQuery.data || []

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Henvendelser ({messages.length})
        </Typography>
        <Button
          startIcon={messagesQuery.isFetching ? <CircularProgress size={18} /> : <RefreshIcon />}
          onClick={() => messagesQuery.refetch()}
          disabled={messagesQuery.isFetching}
        >
          Opdater
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Beskeder fra kontaktformularerne på find-bedemand.dk, nyeste først.
        {messages.length >= MAX_ROWS && ` Viser de seneste ${MAX_ROWS}.`}
      </Typography>

      {(error || messagesQuery.error) && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={error ? () => setError(null) : undefined}>
          {error || (messagesQuery.error as Error).message}
        </Alert>
      )}

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Dato</TableCell>
                <TableCell>Formular</TableCell>
                <TableCell>Navn</TableCell>
                <TableCell>E-mail</TableCell>
                <TableCell>Telefon</TableCell>
                <TableCell>Virksomhed</TableCell>
                <TableCell sx={{ minWidth: 320 }}>Besked</TableCell>
                <TableCell align="center">Slet</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.map((message) => {
                const text = message.message || ''
                const isLong = text.length > PREVIEW_LENGTH
                const isExpanded = expanded.has(message.id)

                return (
                  <TableRow key={message.id} hover sx={{ verticalAlign: 'top' }}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {new Date(message.created_at).toLocaleString('da-DK', {
                        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                      })}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={FORM_LABELS[message.form] || message.form}
                        size="small"
                        color={message.form === 'bedemand' ? 'secondary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {message.name || '–'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {message.email ? <Link href={`mailto:${message.email}`}>{message.email}</Link> : '–'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {message.phone ? <Link href={`tel:${message.phone.replace(/\s/g, '')}`}>{message.phone}</Link> : '–'}
                    </TableCell>
                    <TableCell>{message.company || '–'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {!text ? <em>Ingen besked</em> : isLong && !isExpanded ? `${text.slice(0, PREVIEW_LENGTH).trimEnd()}…` : text}
                      </Typography>
                      {isExpanded && message.page_url && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          Sendt fra:{' '}
                          {/* page_url comes from a public form, so only link real web addresses */}
                          {/^https?:\/\//i.test(message.page_url) ? (
                            <Link href={message.page_url} target="_blank" rel="noopener noreferrer">
                              {message.page_url}
                            </Link>
                          ) : (
                            message.page_url
                          )}
                        </Typography>
                      )}
                      {(isLong || message.page_url) && (
                        <Button size="small" onClick={() => toggleExpanded(message.id)} sx={{ mt: 0.5, px: 0, minWidth: 0 }}>
                          {isExpanded ? 'Vis mindre' : 'Vis mere'}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Slet henvendelse">
                        <IconButton size="small" color="error" onClick={() => setMessageToDelete(message)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
              {messages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Typography color="text.secondary">Ingen henvendelser endnu</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(messageToDelete)}
        onClose={() => !deleteMutation.isPending && setMessageToDelete(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Slet henvendelse?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Er du sikker på at du vil slette henvendelsen fra{' '}
            <strong>{messageToDelete?.name || messageToDelete?.email || 'ukendt afsender'}</strong>?
            <br /><br />
            Dette kan ikke fortrydes.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageToDelete(null)} disabled={deleteMutation.isPending}>
            Annuller
          </Button>
          <Button
            onClick={() => messageToDelete && deleteMutation.mutate(messageToDelete)}
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
            startIcon={deleteMutation.isPending ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleteMutation.isPending ? 'Sletter...' : 'Slet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
